import * as countries from 'smg/countries';
import * as users from 'smg/customer-api/users';
import * as usertaxexemptions from 'smg/customer-api/usertaxexemptions';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import * as cards from './cards';

export const Get = usertaxexemptions.GetUserTaxExemptions;
export const New = usertaxexemptions.NewUserTaxExemption;

export function PromptNew(user: users.UserDetails): Promise<models.UserTaxExemption> {
    return new Promise(resolve => {
        const body = template.Clone('#user-tax-exemption-modal-form-template');

        const reason = body.querySelector('#modal-reason') as HTMLSelectElement;
        const additionalFields = body.querySelector('#modal-additional-fields') as HTMLElement;
        const taxExemptNumber = body.querySelector('#modal-number') as HTMLInputElement;
        const country = body.querySelector('#modal-country') as HTMLSelectElement;
        const state = body.querySelector('#modal-state') as HTMLSelectElement;
        const uploadLabel = body.querySelector('#modal-uploads') as HTMLLabelElement;
        const uploadInput = uploadLabel.querySelector('input[type="file"]') as HTMLInputElement;
        const spinner = uploadLabel.querySelector('.spinner-border') as HTMLElement;

        const nonExempt = reason.querySelector('option[value="0"]');
        nonExempt?.parentElement?.removeChild(nonExempt);

        additionalFields.classList.remove('d-none');

        void countries.Selects(country, state, {
            defaultUnitedStates: true,
            stateParentSelector: 'div',
        });

        const thumbnails = body.querySelector('#modal-uploads-thumbnails') as HTMLElement;

        const m: Modal = new Modal({
            title: 'New Tax Exemption',
            outsideClosable: false,
            body,
            preSubmit: () => {
                if (!u.GetUploadIDs().length) {
                    uploadInput.setCustomValidity('Please upload a valid exemption document.');
                }
            },
            onSubmit: async () => usertaxexemptions.NewUserTaxExemption({
                user,
                number: taxExemptNumber.value,
                countryID: parseInt(country.value, 10),
                stateID: parseInt(state.value, 10),
                uploadIDs: u.GetUploadIDs(),
                userTaxExemptReasonID: parseInt(reason.value, 10),
            }).then(userTaxExemption => {
                toast.Success('Your tax exemption has been added to your account.');
                resolve(userTaxExemption);
            }),
        });

        const u = new uploads.Uploader(uploadLabel, {
            confirmBtn: m.Element.querySelector('[type="submit"]') as HTMLButtonElement,
            thumbnails,
            thumbnailMaxHeight: '5rem',
            start: () => spinner.classList.remove('d-none'),
            stop: () => spinner.classList.add('d-none'),
            onchange: () => {
                uploadInput.setCustomValidity('');
                uploadInput.dispatchEvent(new Event('clearinvalid'));
            },
        });
    });
}

export async function Draw(userTaxExemption: models.UserTaxExemption, el: HTMLElement = template.Clone('#user-tax-exemption-template')): Promise<HTMLElement> {
    const state = await countries.GetState(userTaxExemption.countryID || 0, userTaxExemption.stateID || 0);

    const stateCode = el.querySelector('.user-tax-exemption-state-code');
    if (stateCode) {
        stateCode.textContent = state?.stateCode || 'US';
    }

    const numberEl = el.querySelector('.user-tax-exemption-number');
    if (numberEl) {
        numberEl.textContent = userTaxExemption.number || '';
    }

    const reason = el.querySelector('.user-tax-exemption-reason');
    if (reason) {
        reason.textContent = userTaxExemption.userTaxExemptionReason?.name || '';
    }

    return el;
}

export async function Prompt(user: users.UserDetails): Promise<models.UserTaxExemption | undefined> {
    const userTaxExemptions = await usertaxexemptions.GetUserTaxExemptions({
        'data': 'number,countryID,stateID,userTaxExemptionUploads,userTaxExemptionReasonID,userTaxExemptionReason',
        'sort': 'created desc',
    });
    if (!userTaxExemptions.length) {
        return PromptNew(user);
    }
    return new Promise(resolve => {
        const body = document.createDocumentFragment();

        return (async () => {
            for (const userTaxExemption of userTaxExemptions) {
                await Draw(userTaxExemption).then(card => cards.DrawUse(card, 'Use this tax exemption')).then(h => {
                    h.querySelector('.use')?.addEventListener('click', e => {
                        e.preventDefault();

                        resolve(userTaxExemption);
                        m.Close();
                    });
                    body.appendChild(h);
                });
            }

            const m = new Modal({
                title: 'Select Tax Exemption',
                confirmText: 'Create New Tax Exemption',
                body,
                onSubmit: () => resolve(PromptNew(user)),
                buttons: [{
                    class: 'btn btn-outline-secondary',
                    text: 'Clear',
                    click: () => {
                        resolve(undefined);
                        m.Close();
                    },
                }],
            });
        })();
    });
}
